import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShoppingBag, Check, X, PackageCheck } from "lucide-react";
import api, { fmtErr, peso, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { NeedTenant } from "../components/layout/AppShell";

const STATUS_STYLE = {
  pending: "bg-blue-100 text-blue-700",
  confirmed: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-200 text-slate-600",
};

export default function OrdersPage() {
  const { user, activeTenantId } = useAuth();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/client-orders", { params: status !== "all" ? { status } : {} });
      setOrders(data);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [status]);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, activeTenantId]);

  const setOrderStatus = async (o, s) => {
    try {
      await api.put(`/client-orders/${o.order_id}/status`, { status: s });
      toast.success(`Order ${o.order_number} ${s}${s === "completed" ? " — sale recorded in register" : ""}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  return (
    <div className="space-y-6" data-testid="client-orders-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Client Orders</h1>
        <p className="text-sm text-slate-500">Incoming orders &amp; appointment bookings from the public portal</p>
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          {["all", "pending", "confirmed", "completed", "cancelled"].map((s) => (
            <TabsTrigger key={s} value={s} className="capitalize" data-testid={`orders-filter-${s}`}>{s}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table data-testid="client-orders-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Order</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Fulfillment</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.order_id} data-testid={`order-row-${o.order_id}`}>
                <TableCell>
                  <p className="font-mono text-xs font-semibold text-slate-900">{o.order_number}</p>
                  <p className="text-[11px] text-slate-400">{fmtDateTime(o.created_at)}</p>
                </TableCell>
                <TableCell>
                  <p className="text-sm font-medium text-slate-900">{o.client_name}</p>
                  <p className="text-xs text-slate-400">{o.client_phone || o.client_email}</p>
                </TableCell>
                <TableCell className="text-xs text-slate-600 max-w-52">
                  {o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}
                  {o.note && <p className="text-slate-400 italic mt-0.5">“{o.note}”</p>}
                </TableCell>
                <TableCell>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold capitalize">{o.fulfillment}</span>
                  {o.appointment_at && <p className="text-[11px] text-slate-500 mt-0.5">{fmtDateTime(o.appointment_at)}</p>}
                  {o.delivery_address && <p className="text-[11px] text-slate-500 mt-0.5 max-w-36 truncate">{o.delivery_address}</p>}
                  {o.payment_ref && <p className="font-mono text-[10px] text-slate-400">ref {o.payment_ref}</p>}
                </TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{peso(o.total_amount)}</TableCell>
                <TableCell><span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLE[o.status]}`} data-testid={`order-status-${o.order_id}`}>{o.status}</span></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    {o.status === "pending" && (
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700 h-8" onClick={() => setOrderStatus(o, "confirmed")} data-testid={`order-confirm-${o.order_id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Confirm
                      </Button>
                    )}
                    {o.status === "confirmed" && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => setOrderStatus(o, "completed")} data-testid={`order-complete-${o.order_id}`}>
                        <PackageCheck className="h-3.5 w-3.5 mr-1" /> Complete
                      </Button>
                    )}
                    {["pending", "confirmed"].includes(o.status) && (
                      <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200" onClick={() => setOrderStatus(o, "cancelled")} data-testid={`order-cancel-${o.order_id}`}>
                        <X className="h-3.5 w-3.5 mr-1" /> Cancel
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-slate-400">
                <ShoppingBag className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                No {status !== "all" ? status : ""} orders yet.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
